import { useEffect, useState, useCallback } from 'react'
import { apiFinanceSummary, apiFinanceLedger } from '../server/ServiceCall'
import { AuthState } from '../store/auth'

interface Header {
  CASH:               number
  CREDIT_GRADE:       string
  CREDIT_SCORE:       number
  DEFICIT_STREAK:     number
  SURPLUS_STREAK:     number
  LAST_SETTLED_MONTH: number
}

interface CategoryRow {
  ITEM_TYPE:     number   // 1=수입, 2=지출
  ITEM_CATEGORY: string
  TOTAL_AMOUNT:  number
  CNT:           number
}

interface MonthlyRow {
  MONTH_NO: number
  INCOME:   number
  EXPENSE:  number
  NET:      number
}

interface LedgerRow {
  LEDGER_CODE:   number
  ROUND_NO:      number
  MONTH_NO:      number
  ITEM_TYPE:     number
  ITEM_CATEGORY: string
  ITEM_NAME:     string
  AMOUNT:        number
  BALANCE_AFTER: number
  NOTE:          string | null
  REG_DT:        string
}

const CAT_KO: Record<string, string> = {
  TICKET: '입장권', BROADCAST: '중계권 (주간)', BROADCAST_LUMP: '중계권 시즌 일괄',
  SPONSOR: '스폰서 (주간)', SPONSOR_LUMP: '스폰서 시즌 일괄', CHAMPION: '우승상금',
  TRANSFER_IN: '이적료 수입', WAGE: '주급', FACILITY: '시설 유지', TRANSFER_OUT: '이적료 지출',
  INTEREST: '이자 지출',
}

interface Props {
  auth:     AuthState
  clubCode: number
  seasonNo: number
  nonce?:   number  // 부모가 변경하면 강제 재조회
}

export default function Finance({ auth, clubCode, seasonNo, nonce }: Props) {
  const [header, setHeader]       = useState<Header | null>(null)
  const [cats,   setCats]         = useState<CategoryRow[]>([])
  const [monthly, setMonthly]     = useState<MonthlyRow[]>([])
  const [rows,   setRows]         = useState<LedgerRow[]>([])
  const [loading, setLoading]     = useState(true)
  const [err,    setErr]          = useState('')
  const [filter, setFilter]       = useState<'all' | 'income' | 'expense'>('all')

  const reload = useCallback(async () => {
    setLoading(true); setErr('')
    try {
      const [s, l] = await Promise.all([
        apiFinanceSummary(auth.token, clubCode, seasonNo),
        apiFinanceLedger(auth.token, clubCode, seasonNo, 0, 12),
      ])
      if (s.resultCode !== '00') { setErr(s.resultMessage || '요약 실패'); return }
      if (l.resultCode !== '00') { setErr(l.resultMessage || '원장 실패'); return }
      const so = JSON.parse(s.resultData) as { header: Header, categories: CategoryRow[], monthly: MonthlyRow[] }
      const lo = JSON.parse(l.resultData) as { rows: LedgerRow[] }
      setHeader(so.header); setCats(so.categories ?? []); setMonthly(so.monthly ?? [])
      setRows(lo.rows ?? [])
    } catch (e: any) {
      setErr(e?.message ?? '네트워크 오류')
    } finally { setLoading(false) }
  }, [auth.token, clubCode, seasonNo])

  useEffect(() => { reload() }, [reload, nonce])

  if (loading) return <div className="page-pad">재정 정보 불러오는 중…</div>
  if (err)     return <div className="page-pad msg">{err}</div>

  const totalIncome  = cats.filter(c => c.ITEM_TYPE === 1).reduce((s, c) => s + c.TOTAL_AMOUNT, 0)
  const totalExpense = cats.filter(c => c.ITEM_TYPE === 2).reduce((s, c) => s + c.TOTAL_AMOUNT, 0)
  const seasonNet    = totalIncome - totalExpense

  const filteredRows = rows.filter(r =>
    filter === 'all' ? true : filter === 'income' ? r.ITEM_TYPE === 1 : r.ITEM_TYPE === 2
  )

  return (
    <div className="page-pad finance-page">
      <h2>재정 — 시즌 {seasonNo}</h2>

      {header && (
        <section className="card finance-header">
          <div><label>현금</label><strong>₩ {header.CASH.toLocaleString()}</strong></div>
          <div><label>신용등급</label><strong className={`grade-${header.CREDIT_GRADE}`}>{header.CREDIT_GRADE}</strong></div>
          <div><label>신용 점수</label><strong>{header.CREDIT_SCORE ?? '-'} / 1000</strong></div>
          <div><label>흑자 / 적자 누적</label><strong>{header.SURPLUS_STREAK ?? 0} / {header.DEFICIT_STREAK ?? 0} 개월</strong></div>
          <div><label>마지막 결산 월</label><strong>{header.LAST_SETTLED_MONTH || '-'}</strong></div>
        </section>
      )}

      <section className="card">
        <h3>시즌 누적</h3>
        <div className="finance-totals">
          <div className="t-income"><label>수입</label><strong>+ ₩ {totalIncome.toLocaleString()}</strong></div>
          <div className="t-expense"><label>지출</label><strong>− ₩ {totalExpense.toLocaleString()}</strong></div>
          <div className={seasonNet >= 0 ? 't-net surplus' : 't-net deficit'}>
            <label>순익</label>
            <strong>{seasonNet >= 0 ? '+ ' : '− '}₩ {Math.abs(seasonNet).toLocaleString()}</strong>
          </div>
        </div>
      </section>

      <div className="finance-2col">
        <section className="card">
          <h3>카테고리별</h3>
          <table className="data-table">
            <thead><tr><th>구분</th><th>항목</th><th>금액</th><th>건수</th></tr></thead>
            <tbody>
              {cats.map(c => (
                <tr key={c.ITEM_TYPE + c.ITEM_CATEGORY} className={c.ITEM_TYPE === 1 ? 'row-income' : 'row-expense'}>
                  <td>{c.ITEM_TYPE === 1 ? '수입' : '지출'}</td>
                  <td>{CAT_KO[c.ITEM_CATEGORY] ?? c.ITEM_CATEGORY}</td>
                  <td className="num">₩ {c.TOTAL_AMOUNT.toLocaleString()}</td>
                  <td>{c.CNT}</td>
                </tr>
              ))}
              {cats.length === 0 && <tr><td colSpan={4} className="muted">아직 회계 데이터가 없습니다.</td></tr>}
            </tbody>
          </table>
        </section>

        <section className="card">
          <h3>월별 흑자/적자</h3>
          <table className="data-table">
            <thead><tr><th>월</th><th>수입</th><th>지출</th><th>순익</th></tr></thead>
            <tbody>
              {monthly.map(m => (
                <tr key={m.MONTH_NO} className={m.NET >= 0 ? 'row-surplus' : 'row-deficit'}>
                  <td>{m.MONTH_NO === 0 ? '시작' : `${m.MONTH_NO}월`}</td>
                  <td className="num">₩ {m.INCOME.toLocaleString()}</td>
                  <td className="num">₩ {m.EXPENSE.toLocaleString()}</td>
                  <td className="num"><strong>{m.NET >= 0 ? '+' : ''}{m.NET.toLocaleString()}</strong></td>
                </tr>
              ))}
              {monthly.length === 0 && <tr><td colSpan={4} className="muted">월별 데이터 없음</td></tr>}
            </tbody>
          </table>
        </section>
      </div>

      <section className="card">
        <div className="ledger-head">
          <h3>회계 원장 (최근 500건)</h3>
          <div className="ledger-filter">
            <button className={filter === 'all' ? 'on' : ''} onClick={() => setFilter('all')}>전체</button>
            <button className={filter === 'income' ? 'on' : ''} onClick={() => setFilter('income')}>수입</button>
            <button className={filter === 'expense' ? 'on' : ''} onClick={() => setFilter('expense')}>지출</button>
          </div>
        </div>
        <table className="data-table ledger-table">
          <thead><tr><th>월</th><th>R</th><th>구분</th><th>항목</th><th>금액</th><th>잔액</th><th>비고</th></tr></thead>
          <tbody>
            {filteredRows.map(r => (
              <tr key={r.LEDGER_CODE} className={r.ITEM_TYPE === 1 ? 'row-income' : 'row-expense'}>
                <td>{r.MONTH_NO}</td>
                <td>{r.ROUND_NO}</td>
                <td className="badge-cell"><span className={`type-badge t${r.ITEM_TYPE}`}>{r.ITEM_TYPE === 1 ? '수입' : '지출'}</span></td>
                <td>{r.ITEM_NAME}</td>
                <td className="num">{r.ITEM_TYPE === 1 ? '+' : '−'} ₩ {r.AMOUNT.toLocaleString()}</td>
                <td className="num muted">₩ {r.BALANCE_AFTER.toLocaleString()}</td>
                <td className="muted">{r.NOTE}</td>
              </tr>
            ))}
            {filteredRows.length === 0 && <tr><td colSpan={7} className="muted">표시할 항목이 없습니다.</td></tr>}
          </tbody>
        </table>
      </section>
    </div>
  )
}
